import React, { useEffect, useState } from 'react';
import { Avatar, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, IconButton, Skeleton, Tab, Tabs, Typography } from '@mui/material';
import { MdClose, MdEdit, MdSwapHoriz } from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import ChangeOwnerDialog from '../../components/Products/ChangeOwnerDialog';
import { FONT, StatusChip, authToken, fmtDate, inr, maskAccount, typeLabel } from './supplierShared';

const Row = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.1, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontFamily: mono ? 'monospace' : FONT, fontSize: 13.5, fontWeight: 700, color: '#0f172a', textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);

const Card = ({ title, accent, children }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.25 }}>
    <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14.5, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const Money = ({ label, value, color }) => (
  <Box sx={{ flex: 1, minWidth: 140, p: 2, borderRadius: '12px', background: `${color}14`, border: `1px solid ${color}33` }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', fontWeight: 700 }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 20, fontWeight: 800, color }}>{inr(value)}</Typography>
  </Box>
);

function SupplierView({ open, supplier, onClose, onEdit, onChanged }) {
  const [tab, setTab] = useState(0);
  const [pay, setPay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [picked, setPicked] = useState({});
  const [moving, setMoving] = useState(false);

  useEffect(() => {
    if (!open || !supplier?._id) return;
    setTab(0);
    setPicked({});
    setPay(null);
    setLoading(true);
    getData('supplier/payment/all', { supplierId: supplier._id }, authToken(), false)
      .then((res) => setPay((Array.isArray(res?.data) ? res.data : [])[0] || {}))
      .catch(() => setPay({}))
      .finally(() => setLoading(false));
  }, [open, supplier]);

  if (!supplier) return null;
  const a = supplier.billingAddress || {};
  const b = supplier.bankDetails || {};
  const address = [a.street, a.city, a.state, a.pincode, a.country].filter(Boolean).join(', ');
  const products = supplier.products || [];
  const prodOf = (p) => (p.productId && typeof p.productId === 'object' ? p.productId : {});
  const pickedList = products.map(prodOf).filter((p) => p._id && picked[p._id]).map((p) => ({ _id: p._id, title: p.title }));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, px: 3, py: 2.25, background: 'linear-gradient(135deg,#6366f1,#4338ca)', color: '#fff' }}>
        <Avatar sx={{ width: 52, height: 52, bgcolor: 'rgba(255,255,255,.2)', fontWeight: 800 }}>{(String(supplier.name || 'S').trim()[0] || 'S').toUpperCase()}</Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 20, lineHeight: 1.2 }}>{supplier.name}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, opacity: 0.85 }}>{supplier.supplierId || '—'} · {typeLabel(supplier.businessType)}</Typography>
        </Box>
        <StatusChip status={supplier.status} />
        <IconButton onClick={onClose} size="small" sx={{ color: '#fff' }}><MdClose size={20} /></IconButton>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ background: '#fff', px: 2, borderBottom: '1px solid #e2e8f0', '& .MuiTab-root': { textTransform: 'none', fontFamily: FONT, fontWeight: 700 } }}>
        <Tab label="Overview" />
        <Tab label={`Products (${products.length})`} />
        <Tab label="Bank & payments" />
      </Tabs>

      <DialogContent sx={{ p: 3 }}>
        {tab === 0 && (
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
            <Card title="Contact" accent="#6366f1">
              <Row label="Contact person" value={supplier.contactPerson} />
              <Row label="Phone" value={supplier.phone} />
              <Row label="Email" value={supplier.email} />
              <Row label="Added on" value={fmtDate(supplier.createdAt)} />
            </Card>
            <Card title="Tax & license" accent="#0ea5e9">
              <Row label="PAN" value={supplier.panNumber} mono />
              <Row label="GST" value={supplier.gstNumber} mono />
              <Row label="Trade license" value={supplier.tradeLicense} />
            </Card>
            <Box sx={{ gridColumn: { md: '1 / -1' } }}>
              <Card title="Billing address" accent="#10b981">
                <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#0f172a' }}>{address || '—'}</Typography>
              </Card>
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Card title="Supplied products" accent="#ec4899">
            {products.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No products linked.</Typography>}
            {products.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b', flex: 1 }}>{pickedList.length ? `${pickedList.length} selected` : 'Select products to move to another owner.'}</Typography>
                <Button size="small" variant="contained" startIcon={<MdSwapHoriz />} disabled={!pickedList.length} onClick={() => setMoving(true)} sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', '&:hover': { background: 'rgb(255 190 0)' } }}>Change owner</Button>
              </Box>
            )}
            {products.map((p, i) => {
              const prod = prodOf(p);
              return (
                <Box key={prod._id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.1, borderBottom: i < products.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                  <Checkbox size="small" disabled={!prod._id} checked={!!picked[prod._id]} onChange={() => setPicked((m) => ({ ...m, [prod._id]: !m[prod._id] }))} sx={{ p: 0.5 }} />
                  <Typography sx={{ flex: 1, fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{prod.title || 'Product'}</Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#475569' }}>MOQ <b>{p.moq || 0}</b></Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#475569' }}>Lead <b>{p.leadTime || 0}</b> days</Typography>
                </Box>
              );
            })}
          </Card>
        )}

        {tab === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              {loading ? [0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={74} sx={{ flex: 1, minWidth: 140 }} />) : (
                <>
                  <Money label="Total invoiced" value={pay?.totalFinalInvoiceAmount} color="#6366f1" />
                  <Money label="Paid" value={pay?.totalPaidAmount} color="#16a34a" />
                  <Money label="Pending" value={pay?.totalPendingAmount} color="#dc2626" />
                </>
              )}
            </Box>
            <Card title="Bank details" accent="#f59e0b">
              <Row label="Account holder" value={b.accountHolder} />
              <Row label="Account number" value={maskAccount(b.accountNumber)} mono />
              <Row label="IFSC" value={b.ifsc} mono />
              <Row label="SWIFT" value={b.swift} mono />
              <Row label="UPI ID" value={b.upiId} />
              <Row label="Preferred mode" value={b.preferredMode} />
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
        <Button onClick={() => onEdit(supplier)} startIcon={<MdEdit />} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', '&:hover': { background: 'rgb(255 190 0)' } }}>Edit</Button>
      </DialogActions>
      <ChangeOwnerDialog open={moving} products={pickedList} onClose={() => setMoving(false)} onDone={() => onChanged?.()} />
    </Dialog>
  );
}

export default SupplierView;
